import { computed, Ref, ref, watch } from 'vue'

import { isIncidentDeleted } from '@collector/shared-ui-domain'
import { Incident } from '@collector/sportsapi-client'
import { useIncidents } from '@desktop/views/Relation/composables'

import { injectLastIncidentsConfig } from '../Sports/provide-inject/lastIncidentsConfig'

type UseLastIncident = {
  lastNonDeletedIncident: Ref<Incident | undefined>
  lastNonDeletedIncidentWithPosition: Ref<Incident | undefined>
}

export function useLastIncident(): UseLastIncident {
  // inject
  const config = injectLastIncidentsConfig()

  // composables
  const { incidents, incidentsUpdate } = useIncidents()

  // state
  const lastNonDeletedIncident = ref(
    findLastNonDeletedIncident(incidents.value),
  )
  const lastNonDeletedIncidentWithPosition = config?.incidentsUpdate
    ? ref(findLastNonDeletedIncidentWithPosition(incidents.value))
    : computed(() => findLastNonDeletedIncidentWithPosition(incidents.value))

  // methods
  function findLastNonDeletedIncident(
    incidents: Incident[],
  ): Incident | undefined {
    return incidents.findLast((incident) => !isIncidentDeleted(incident))
  }

  function findLastNonDeletedIncidentWithPosition(
    incidents: Incident[],
  ): Incident | undefined {
    return incidents.findLast((incident) => hasPosition(incident))
  }

  function hasPosition(incident: Incident): boolean {
    return (
      !isIncidentDeleted(incident) &&
      typeof incident.x_pos === 'number' &&
      typeof incident.y_pos === 'number'
    )
  }

  // init
  watch(incidentsUpdate, (newVal) => {
    const last = newVal?.at(-1)
    if (!last) return
    const { data } = last

    if (config?.incidentsUpdate)
      return config.incidentsUpdate(
        data,
        incidents,
        lastNonDeletedIncident,
        lastNonDeletedIncidentWithPosition,
        findLastNonDeletedIncident,
        findLastNonDeletedIncidentWithPosition,
        hasPosition,
        isIncidentDeleted,
      )

    if (
      data.action === 'delete' &&
      data.id === lastNonDeletedIncident.value?.id
    ) {
      lastNonDeletedIncident.value = findLastNonDeletedIncident(incidents.value)
    } else if (data.action === 'insert') {
      lastNonDeletedIncident.value = data
    }
  })

  return {
    lastNonDeletedIncidentWithPosition,
    lastNonDeletedIncident,
  }
}
